import { Outlet, Route, useOutletContext } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { RouteType } from './route.t';
import {
	AtencionCiudadano,
	Home,
	Presentacion,
	JuntaDirectiva,
	Departamentos,
	Notarios,
	VisitaVirtual,
	Articulo,
	ActualidadCategoria,
	Historia,
	Hemeroteca,
	Catalogo,
	CatalogoCategoria,
	Producto,
	BuscadorNotarios,
	LibreEleccionNotarios,
	NotariosGuardia,
	AcademiaOpositores,
	OrganismosInternacionales,
	EncuentraEscritura,
	NuevasActuacionesNotariales,
	ActuacionNotarialPage,
	EnlacesInteres,
	ProgramacionAcademica,
	CatalogoSolicitud,
} from '@/pages';
import { Layout, InnerLayout, type OutletCtx } from '@/containers';
import { REL, SECTIONS } from '@/constants/routes.constants';
import {
	CopiasArchivosProtocolo,
	ExpedientesMatrimoniales,
	LegalizacionApostillas,
	Oferta,
	PortalEmpleo,
} from '@/pages/Servicios';
import { ConferenciasLayout } from '@/containers/ConferenciasLayout';

const Group = () => {
	const parentCtx = useOutletContext<OutletCtx | undefined>();
	return <Outlet context={parentCtx} />;
};

const homeRoute: RouteType[] = [{ index: true, element: <Home /> }];
const innerRoutes: RouteType[] = [
	// EL COLEGIO
	{
		path: SECTIONS.COLEGIO,
		element: <Group />,
		handle: { headerActive: SECTIONS.COLEGIO },
		children: [
			{ path: REL.COLEGIO.PRESENTACION, element: <Presentacion /> },
			{ path: REL.COLEGIO.JUNTA_DIRECTIVA, element: <JuntaDirectiva /> },
			{ path: REL.COLEGIO.DEPARTAMENTOS, element: <Departamentos /> },
			{ path: REL.COLEGIO.NOTARIOS, element: <Notarios /> },
			{ path: REL.COLEGIO.VISITA_VIRTUAL, element: <VisitaVirtual /> },
		],
	},
	// ACTUALIDAD
	{
		path: SECTIONS.ACTUALIDAD,
		element: <Group />,
		handle: { headerActive: SECTIONS.ACTUALIDAD },
		children: [
			{ path: REL.ACTUALIDAD.BY_CATEGORY, element: <ActualidadCategoria /> },
			{ path: REL.ACTUALIDAD.BY_POST, element: <Articulo /> },
		],
	},
	// ACADEMIA MATRITENSE
	{
		path: SECTIONS.ACADEMIA_MATRITENSE,
		element: <Group />,
		handle: { headerActive: SECTIONS.ACADEMIA_MATRITENSE },
		children: [
			{ path: REL.ACADEMIA_MATRITENSE.HISTORIA, element: <Historia /> },
			{ path: REL.ACADEMIA_MATRITENSE.PROGRAMACION_ACADEMICA, element: <ProgramacionAcademica /> },
			{ path: REL.ACADEMIA_MATRITENSE.HEMEROTECA, element: <Hemeroteca /> },
			{ path: REL.ACADEMIA_MATRITENSE.CATALOGO, element: <Catalogo /> },
			{ path: REL.ACADEMIA_MATRITENSE.CATALOGO_BY_CATEGORY, element: <CatalogoCategoria /> },
			{ path: REL.ACADEMIA_MATRITENSE.CATALOGO_BY_PRODUCT, element: <Producto /> },
			{ path: REL.ACADEMIA_MATRITENSE.CATALOGO_SOLICITAR, element: <CatalogoSolicitud /> },
		],
	},
	// INFORMACIÓN
	{
		path: SECTIONS.INFORMACION,
		element: <Group />,
		handle: { headerActive: SECTIONS.INFORMACION },
		children: [
			{ path: REL.INFORMACION.BUSCADOR_NOTARIOS, element: <BuscadorNotarios /> },
			{ path: REL.INFORMACION.LIBRE_ELECCION_NOTARIOS, element: <LibreEleccionNotarios /> },
			{ path: REL.INFORMACION.NOTARIOS_DE_GUARDIA, element: <NotariosGuardia /> },
			{ path: REL.INFORMACION.ACADEMIA_OPOSITORES, element: <AcademiaOpositores /> },
			{ path: REL.INFORMACION.ORGANISMOS_INTERNACIONALES, element: <OrganismosInternacionales /> },
			{ path: REL.INFORMACION.ENCUENTRA_ESCRITURA, element: <EncuentraEscritura /> },
			{ path: REL.INFORMACION.NUEVAS_ACTUACIONES_NOTARIALES, element: <NuevasActuacionesNotariales /> },
			{ path: REL.INFORMACION.NUEVAS_ACTUACIONES_NOTARIALES_BY_PAGE, element: <ActuacionNotarialPage /> },
			{ path: REL.INFORMACION.ENLACES_INTERES, element: <EnlacesInteres /> },
		],
	},
	// SERVICIOS
	{
		path: SECTIONS.SERVICIOS,
		element: <Group />,
		handle: { headerActive: SECTIONS.SERVICIOS },
		children: [
			{ path: REL.SERVICIOS.LEGALIZACION_APOSTILLAS, element: <LegalizacionApostillas /> },
			{ path: REL.SERVICIOS.COPIAS_ARCHIVOS_PROTOCOLO, element: <CopiasArchivosProtocolo /> },
			{ path: REL.SERVICIOS.ATENCION_CIUDADANO, element: <AtencionCiudadano /> },
			{ path: REL.SERVICIOS.EXPEDIENTES_MATRIMONIALES, element: <ExpedientesMatrimoniales /> },
			{ path: REL.SERVICIOS.PORTAL_EMPLEO, element: <PortalEmpleo /> },
			{ path: REL.SERVICIOS.PORTAL_EMPLEO_BY_OFERTA, element: <Oferta /> },
		],
	},
];

const academiaRoutes: RouteType[] = [
	{
		path: 'academia-matritense/hemeroteca',
		element: <Hemeroteca />,
	},
];

const renderRoutes = (routes: RouteType[]): ReactNode =>
	routes.map((route, i) => {
		if ('index' in route) {
			return <Route key="index" index element={route.element} />;
		}
		return (
			<Route key={route.path} path={route.path} element={route.element}>
				{route.children ? renderRoutes(route.children) : null}
			</Route>
		);
	});

const generateHomeRoutes = (routes: RouteType[]): ReactNode => (
	<Route element={<Layout />}>{renderRoutes(routes)}</Route>
);

const generateInnerRoutes = (routes: RouteType[]): ReactNode => (
	<Route element={<InnerLayout />}>{renderRoutes(routes)}</Route>
);

const generateConferenciasRoutes = (routes: RouteType[]): ReactNode => (
	<Route element={<ConferenciasLayout />}>{renderRoutes(routes)}</Route>
);

export { generateHomeRoutes, generateInnerRoutes, generateConferenciasRoutes, homeRoute, innerRoutes, academiaRoutes };
